import { filter, intersection, isEmpty, map, sortBy } from "lodash/fp";

import * as api from "@readai/common/api";
import { parseUTCDateTimeToTimezoneFromString } from "@readai/common/utils/datetimeUtils";

import type {
  ApiScheduleRecommendation,
  ApiScheduleRecommendationRequestBody,
  ApiScheduleRecommendationResponse,
  ScheduleRecommendation,
  ScheduleRecommendationBestOptions,
  ScheduleRecommendationDailyAvailability,
  ScheduleRecommendationLabel,
} from "@app/booking/types/scheduleRecommendationTypes";

const isGoodTime = (labels: ScheduleRecommendationLabel[]) =>
  // eslint-disable-next-line lingui/no-unlocalized-strings
  !isEmpty(intersection(["Best", "Good"], labels));

const parseScheduleRecommendation =
  (timezone?: string) =>
  ({ timeRange, ...recommendation }: ApiScheduleRecommendation) => ({
    ...recommendation,
    isRecommended: isGoodTime(recommendation.labels),
    time: parseUTCDateTimeToTimezoneFromString(timeRange.start, timezone),
  });

const getScheduleRecommendations = async (
  ownerVanityId: string,
  linkId: string,
  body: ApiScheduleRecommendationRequestBody,
): Promise<ApiScheduleRecommendationResponse> => {
  return api.post(
    `/rss_links/${ownerVanityId}/${linkId}/recommendations`,
    body,
    {
      withCreds: true,
    },
  );
};

const parseAndSortRecommendations = (
  recommendations: ApiScheduleRecommendation[],
  timezone?: string,
) => {
  const parsedRecommendations = map(
    parseScheduleRecommendation(timezone),
    recommendations,
  );
  return sortBy((rec) => rec.time.toMillis(), parsedRecommendations);
};

/** Returns recommendations for a single recommendation window */
export const listForPeriod = async (
  ownerVanityId: string,
  linkId: string,
  body: ApiScheduleRecommendationRequestBody,
): Promise<ScheduleRecommendation[]> => {
  const response = await getScheduleRecommendations(
    ownerVanityId,
    linkId,
    body,
  );

  const timezone = body.proposed.timeRange.tzName;

  const recommendations =
    response.recommendationWindowResults[0]?.scheduleRecommendations ?? [];

  return parseAndSortRecommendations(recommendations, timezone);
};

export const getAvailability = async (
  ownerVanityId: string,
  linkId: string,
  body: ApiScheduleRecommendationRequestBody,
): Promise<ScheduleRecommendationDailyAvailability> => {
  const response = await getScheduleRecommendations(
    ownerVanityId,
    linkId,
    body,
  );

  const availability =
    response.recommendationWindowResults[0]?.dailyAvailability ?? [];

  return availability.reduce((acc, curr) => {
    acc[curr.date] = { availabilityScore: curr.nFreeSlots };
    return acc;
  }, {} as ScheduleRecommendationDailyAvailability);
};

export const listBestOptions = async (
  ownerVanityId: string,
  linkId: string,
  body: ApiScheduleRecommendationRequestBody,
): Promise<ScheduleRecommendationBestOptions> => {
  const response = await getScheduleRecommendations(
    ownerVanityId,
    linkId,
    body,
  );

  const timezone = body.proposed.timeRange.tzName;

  return response.recommendationWindowResults.reduce(
    (acc, curr) => {
      const windowLabel = curr.recommendationWindow
        .label as keyof ScheduleRecommendationBestOptions;
      acc[windowLabel] = parseAndSortRecommendations(
        filter((rec) => isGoodTime(rec.labels), curr.scheduleRecommendations),
        timezone,
      );
      return acc;
    },
    { thisWeek: [], nextWeek: [] } as ScheduleRecommendationBestOptions,
  );
};
